import React, { useEffect, useReducer } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * Single global self-referencing canonical for every route.
 * Mounted once at the root of the app — works automatically for every
 * existing and future page. Format: https://biscuitbrand.com<pathname>.
 *
 * Configurable canonical origin via REACT_APP_CANONICAL_URL.
 *
 * Per-page override:
 *   Pages that need a different canonical than their pathname (e.g. a
 *   product page reached via /product/<id> that should canonicalise to
 *   /product/<slug>) call setRouteCanonical(absoluteUrl). The override
 *   is automatically cleared on the next route change so it can never
 *   leak across navigations.
 */
const CANONICAL_ORIGIN = (process.env.REACT_APP_CANONICAL_URL || 'https://biscuitbrand.com').replace(/\/+$/, '');

const buildCanonical = (pathname) => {
    let p = (pathname || '/').split('?')[0].split('#')[0];
    if (!p.startsWith('/')) p = '/' + p;
    p = p.replace(/\/{2,}/g, '/');
    if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
    return `${CANONICAL_ORIGIN}${p}`;
};

// --- Module-level override mechanism (per-route) -------------------------
// Pages call setRouteCanonical(<absolute-url>) in a useEffect when they
// know the canonical should not be the current pathname. The override is
// auto-cleared whenever the route changes.
let _override = null;
const _subs = new Set();
const _notify = () => { _subs.forEach((fn) => fn()); };

export const setRouteCanonical = (absoluteUrl) => {
    if (!absoluteUrl || typeof absoluteUrl !== 'string') return;
    if (_override === absoluteUrl) return;
    _override = absoluteUrl;
    _notify();
};

export const clearRouteCanonical = () => {
    if (_override === null) return;
    _override = null;
    _notify();
};

export const toAbsoluteCanonical = (input) => {
    if (!input) return null;
    if (/^https?:\/\//i.test(input)) return input.replace(/\/+$/, '') || input;
    let p = input;
    if (!p.startsWith('/')) p = '/' + p;
    p = p.split('?')[0].split('#')[0].replace(/\/{2,}/g, '/');
    if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
    return `${CANONICAL_ORIGIN}${p}`;
};

const Canonical = () => {
    const location = useLocation();
    const [, force] = useReducer((n) => n + 1, 0);

    // Subscribe to override changes triggered by setRouteCanonical.
    useEffect(() => {
        _subs.add(force);
        return () => _subs.delete(force);
    }, []);

    // Auto-clear override on every route change so it never leaks
    // between pages.
    useEffect(() => {
        if (_override !== null) {
            _override = null;
            _notify();
        }
    }, [location.pathname]);

    const url = _override || buildCanonical(location.pathname);
    return (
        <Helmet>
            <link rel="canonical" href={url} />
            <meta property="og:url" content={url} />
        </Helmet>
    );
};

export default Canonical;
